import { useSearchParams } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createEffect, createMemo } from "solid-js";
import type { FlowsView } from "../../enums/scan";
import { FLOWS_VIEW } from "../../enums/scan";
import { singleParam } from "../../ui/searchParam";
import {
  resolveSelectedFlowPath,
  shouldReplaceFlowParam,
} from "./flowRouteSelection";
import { buildJourneyPreview } from "./journeyModel";
import { buildPriorityFlowPreview, flowForPath } from "./priorityFlows";

export function useFlowPaths(
  data: () => OverviewResponse,
  selectedPageId: () => string | null | undefined,
) {
  const [search, setSearch] = useSearchParams();
  const preview = createMemo(() => buildJourneyPreview(data()));
  const priorityFlows = createMemo(() =>
    buildPriorityFlowPreview(data(), preview().paths),
  );
  const view = createMemo<FlowsView>(() =>
    singleParam(search.view) === "paths"
      ? FLOWS_VIEW.PATHS
      : FLOWS_VIEW.STRUCTURE,
  );
  const selectedPath = createMemo(() => {
    const pathId = singleParam(search.flow);
    return resolveSelectedFlowPath(preview().paths, priorityFlows(), pathId);
  });
  const selectedFlow = createMemo(() =>
    flowForPath(priorityFlows(), selectedPath()?.id),
  );

  createEffect(() => {
    const rawView = singleParam(search.view);
    if (rawView && rawView !== "structure" && rawView !== "paths") {
      setSearch({ view: "structure" }, { replace: true });
      return;
    }
    if (view() !== FLOWS_VIEW.PATHS) return;
    const path = selectedPath();
    if (!path) {
      if (singleParam(search.flow)) {
        setSearch({ flow: undefined }, { replace: true });
      }
      return;
    }
    const update: { flow?: string; page?: string } = {};
    if (shouldReplaceFlowParam(singleParam(search.flow), path)) {
      update.flow = path.id;
    }
    if (!selectedPageId() && path.steps[0]) {
      update.page = path.steps[0].page.id;
    }
    if (update.flow || update.page) {
      setSearch(update, { replace: true });
    }
  });

  const setView = (nextView: FlowsView) => {
    if (nextView === FLOWS_VIEW.STRUCTURE) {
      setSearch({ view: nextView });
      return;
    }
    const path = selectedPath();
    const currentPageId = selectedPageId();
    const nextPageId = path?.steps.some(
      (step) => step.page.id === currentPageId,
    )
      ? currentPageId
      : path?.steps[0]?.page.id;
    setSearch({ view: nextView, flow: path?.id, page: nextPageId });
  };
  const selectPath = (pathId: string) => {
    const path = preview().paths.find((candidate) => candidate.id === pathId);
    if (!path) return;
    const currentPageId = selectedPageId();
    const nextPageId = path.steps.some((step) => step.page.id === currentPageId)
      ? currentPageId
      : (path.steps[0]?.page.id ?? undefined);
    setSearch({ view: "paths", flow: path.id, page: nextPageId });
  };
  const selectFlow = (flowId: string) => {
    const flow = priorityFlows().all.find(
      (candidate) => candidate.id === flowId,
    );
    if (flow) {
      selectPath(flow.representativePath.id);
    }
  };

  return {
    preview,
    priorityFlows,
    view,
    setView,
    selectedPath,
    selectedFlow,
    selectFlow,
    selectPath,
  };
}
